import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useU2000AuthContext } from '../../../../../u2000Core/context/auth/U2000UseAuthContext';
import { useToast } from '../../../../../u2000Core/context/toast/U2000ToastContext';
import useU2000LocalStorage from '../../../../../u2000Core/hooks/U2000UseLocalStorage';
import { U3500ROLES } from '../../../../../u3500GedDocumentSearch/U3500';
import { useU3500DeliverableChecklistHandlers } from '../../../../../u3500GedDocumentSearch/hooks/useU3500DeliverableChecklistHandlers';
import { useU3500DocumentContentHandlers } from '../../../../../u3500GedDocumentSearch/hooks/useU3500DocumentContentHandlers';
import { useU3500DocumentMetaHandlers } from '../../../../../u3500GedDocumentSearch/hooks/useU3500DocumentMetaHandlers';
import { useU3500ProjectDocumentTreeHandlers } from '../../../../../u3500GedDocumentSearch/hooks/useU3500ProjectDocumentTreeHandlers';
import { useU3500ProjectScanHandlers } from '../../../../../u3500GedDocumentSearch/hooks/useU3500ProjectScanHandlers';
import type {
  U3500DeliverableChecklistResponse,
  U3500DocumentMeta,
  U3500DocumentTreeNode,
  U3500DocumentTreeResponse,
} from '../../../../../u3500GedDocumentSearch/U3500Service';
import { NS } from '../../../../U3121';
import {
  U3121_DOCUMENTS_SPLIT_DEFAULT_LEFT_PCT,
  u3121ClampDocumentsSplitLeftPercent,
  u3121DocumentsSplitStorageKey,
  u3121NormalizeDocumentsSplitLeftPct,
} from '../../../../tools/u3121DocumentsSplitWidth';
import {
  u3121CollectAvailableTypeTags,
  u3121FilterFlatDocuments,
  u3121FlattenDocumentTree,
  type U3121DocumentNameMatchMode,
  type U3121FlatDocumentItem,
} from '../../../../tools/u3121FlattenDocumentTree';
import U3121NasSyncStatus from '../../../common/U3121NasSyncStatus';
import U3121ProjectDeliverableChecklist from './U3121ProjectDeliverableChecklist';
import U3121ProjectDocumentFlatList from './U3121ProjectDocumentFlatList';
import U3121ProjectDocumentPreviewExpandDialog from './U3121ProjectDocumentPreviewExpandDialog';
import U3121ProjectDocumentPreviewPanel from './U3121ProjectDocumentPreviewPanel';
import U3121ProjectDocumentTree from './U3121ProjectDocumentTree';

export interface U3121ProjectDocumentsBodyProps {
  projectId: string;
  /** Mode Documents élargi (BL-0488) — bouton Entrée masqué si déjà actif. */
  documentsExpanded?: boolean;
  onEnterDocumentsExpanded?: () => void;
}

type PreviewMode = 'idle' | 'loading' | 'pdf' | 'local' | 'error';
type DocumentsViewMode = 'tree' | 'list';

const NAME_MATCH_MODES: U3121DocumentNameMatchMode[] = ['contains', 'startswith', 'endswith'];

const nameMatchModeLabelKey = (mode: U3121DocumentNameMatchMode): string => {
  if (mode === 'startswith') {
    return 'lblCndStartsWith';
  }
  if (mode === 'endswith') {
    return 'lblCndEndsWith';
  }
  return 'lblCndContains';
};

/** Poll nasSync tant que status === pending (BL-0483). */
const NAS_SYNC_POLL_INTERVAL_MS = 5_000;
const NAS_SYNC_POLL_MAX_ATTEMPTS = 24;

const documentTypeTagLabelKey = (tag: string): string => {
  if (tag === 'Autre') {
    return 'lblDocumentTypeTagAutre';
  }
  return `lblDocumentTypeTag${tag}`;
};

const U3121ProjectDocumentsBody: React.FC<U3121ProjectDocumentsBodyProps> = ({
  projectId,
  documentsExpanded = false,
  onEnterDocumentsExpanded,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { auth, hasRole } = useU2000AuthContext();
  const canScan = hasRole(U3500ROLES.ADD);
  const splitStorageKey = useMemo(
    () => u3121DocumentsSplitStorageKey(auth.user.userName, auth.user.organizationId),
    [auth.user.userName, auth.user.organizationId]
  );
  const [leftPctStored, setLeftPctStored] = useU2000LocalStorage<number>(
    splitStorageKey,
    U3121_DOCUMENTS_SPLIT_DEFAULT_LEFT_PCT
  );
  const leftPct = u3121NormalizeDocumentsSplitLeftPct(leftPctStored);
  const [isSplitResizing, setIsSplitResizing] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const { loadTree } = useU3500ProjectDocumentTreeHandlers();
  const { loadChecklist } = useU3500DeliverableChecklistHandlers();
  const { loadMeta } = useU3500DocumentMetaHandlers();
  const { loadContent } = useU3500DocumentContentHandlers();
  const { startProjectScan } = useU3500ProjectScanHandlers();

  const [tree, setTree] = useState<U3500DocumentTreeResponse | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeLoaded, setTreeLoaded] = useState(false);
  const [checklist, setChecklist] = useState<U3500DeliverableChecklistResponse | null>(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('idle');
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [meta, setMeta] = useState<U3500DocumentMeta | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<DocumentsViewMode>('tree');
  const [nameQuery, setNameQuery] = useState('');
  const [nameMatchMode, setNameMatchMode] = useState<U3121DocumentNameMatchMode>('contains');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [expandPreviewOpen, setExpandPreviewOpen] = useState(false);
  const pdfUrlRef = useRef<string | null>(null);
  const loadSeqRef = useRef(0);
  const nasSyncPollAttemptsRef = useRef(0);

  useEffect(() => {
    void i18next.loadNamespaces(['u3500']);
  }, []);

  useEffect(() => {
    if (previewMode !== 'pdf' || !pdfObjectUrl) {
      setExpandPreviewOpen(false);
    }
  }, [previewMode, pdfObjectUrl]);

  const revokePdfUrl = useCallback(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfObjectUrl(null);
  }, []);

  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, []);

  const refreshChecklist = useCallback(
    async (id: string) => {
      setChecklistLoading(true);
      try {
        const result = await loadChecklist(id);
        setChecklist(result);
      } finally {
        setChecklistLoading(false);
      }
    },
    [loadChecklist]
  );

  const refreshTree = useCallback(
    async (id: string, options?: { silent?: boolean }) => {
      if (!options?.silent) {
        setTreeLoading(true);
      }
      try {
        const result = await loadTree(id);
        setTree(result);
        setTreeLoaded(true);
        return result;
      } finally {
        if (!options?.silent) {
          setTreeLoading(false);
        }
      }
    },
    [loadTree]
  );

  useEffect(() => {
    let cancelled = false;
    setTree(null);
    setTreeLoaded(false);
    setChecklist(null);
    setSelectedPath(null);
    setPreviewMode('idle');
    setMeta(null);
    setFileName(null);
    setErrorMessage(null);
    setViewMode('tree');
    setNameQuery('');
    setNameMatchMode('contains');
    setSelectedTags([]);
    nasSyncPollAttemptsRef.current = 0;
    revokePdfUrl();

    if (!projectId?.trim()) {
      return;
    }

    setTreeLoading(true);
    setChecklistLoading(true);
    void (async () => {
      const [treeResult, checklistResult] = await Promise.all([
        loadTree(projectId),
        loadChecklist(projectId),
      ]);
      if (cancelled) {
        return;
      }
      setTree(treeResult);
      setTreeLoaded(true);
      setTreeLoading(false);
      setChecklist(checklistResult);
      setChecklistLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, loadTree, loadChecklist, revokePdfUrl]);

  /** Poll léger tant que nasSync.status === pending (max ~2 min). */
  useEffect(() => {
    if (!projectId?.trim() || tree?.nasSync?.status !== 'pending') {
      nasSyncPollAttemptsRef.current = 0;
      return;
    }

    const timer = window.setInterval(() => {
      if (nasSyncPollAttemptsRef.current >= NAS_SYNC_POLL_MAX_ATTEMPTS) {
        window.clearInterval(timer);
        return;
      }
      nasSyncPollAttemptsRef.current += 1;
      void refreshTree(projectId, { silent: true });
    }, NAS_SYNC_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [projectId, tree?.nasSync?.status, refreshTree]);

  const handleScanFolder = useCallback(async () => {
    if (!projectId?.trim() || scanning) {
      return;
    }
    setScanning(true);
    try {
      const consigne = await startProjectScan(projectId);
      if (consigne) {
        toast.success(t('msgProjectScanQueued', { ns: NS }));
        nasSyncPollAttemptsRef.current = 0;
        await Promise.all([refreshTree(projectId), refreshChecklist(projectId)]);
      }
    } finally {
      setScanning(false);
    }
  }, [projectId, scanning, startProjectScan, t, toast, refreshTree, refreshChecklist]);

  const handleRefreshChecklist = useCallback(() => {
    if (!projectId?.trim()) {
      return;
    }
    void refreshChecklist(projectId);
  }, [projectId, refreshChecklist]);

  const openDocumentPreview = useCallback(
    async (documentId: string, displayName?: string | null, relativePath?: string | null) => {
      const seq = ++loadSeqRef.current;
      setSelectedPath(relativePath ?? null);
      setFileName(displayName ?? null);
      setMeta(null);
      setErrorMessage(null);
      revokePdfUrl();
      setPreviewMode('loading');

      const documentMeta = await loadMeta(documentId);
      if (seq !== loadSeqRef.current) {
        return;
      }
      if (!documentMeta) {
        setPreviewMode('error');
        setErrorMessage(t('msgDocumentPreviewError', { ns: NS }));
        return;
      }
      setMeta(documentMeta);
      setFileName(documentMeta.fileName || displayName || null);
      setSelectedPath(documentMeta.relativePath || relativePath || null);

      if (documentMeta.previewable === true) {
        const blob = await loadContent(documentId);
        if (seq !== loadSeqRef.current) {
          return;
        }
        if (!blob) {
          setPreviewMode('error');
          setErrorMessage(t('msgDocumentPreviewError', { ns: NS }));
          return;
        }
        const url = URL.createObjectURL(blob);
        pdfUrlRef.current = url;
        setPdfObjectUrl(url);
        setPreviewMode('pdf');
        return;
      }

      setPreviewMode('local');
    },
    [loadContent, loadMeta, revokePdfUrl, t]
  );

  const handleSelectFile = useCallback(
    async (node: U3500DocumentTreeNode) => {
      if (node.kind !== 'file' || !node.documentId) {
        return;
      }
      await openDocumentPreview(node.documentId, node.fileName ?? node.name, node.relativePath);
    },
    [openDocumentPreview]
  );

  const handleSelectFlatFile = useCallback(
    (item: U3121FlatDocumentItem) => {
      void openDocumentPreview(item.documentId, item.fileName ?? item.name, item.relativePath);
    },
    [openDocumentPreview]
  );

  const handleOpenDeliverableDocument = useCallback(
    (documentId: string, matchedFileName?: string | null) => {
      void openDocumentPreview(documentId, matchedFileName);
    },
    [openDocumentPreview]
  );

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  }, []);

  const handleSplitPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsSplitResizing(true);
  }, []);

  useEffect(() => {
    if (!isSplitResizing) {
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const container = splitContainerRef.current;
      if (!container) {
        return;
      }
      const rect = container.getBoundingClientRect();
      const nextPct = u3121ClampDocumentsSplitLeftPercent(
        event.clientX - rect.left,
        rect.width
      );
      setLeftPctStored(nextPct);
    };

    const onPointerUp = () => {
      setIsSplitResizing(false);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [isSplitResizing, setLeftPctStored]);

  const nodes = tree?.nodes ?? [];
  const isEmpty = treeLoaded && !treeLoading && tree !== null && nodes.length === 0;
  const showNavigator = !treeLoading && tree !== null && nodes.length > 0;

  const flatItems = useMemo(() => u3121FlattenDocumentTree(nodes), [nodes]);
  const availableTags = useMemo(() => u3121CollectAvailableTypeTags(flatItems), [flatItems]);
  const filteredFlatItems = useMemo(
    () => u3121FilterFlatDocuments(flatItems, { nameQuery, nameMatchMode, selectedTags }),
    [flatItems, nameQuery, nameMatchMode, selectedTags]
  );

  return (
    <div
      className={`mt-2 flex flex-col gap-3 ${documentsExpanded ? 'min-h-0 flex-1 overflow-hidden' : ''}`}
    >
      <U3121ProjectDeliverableChecklist
        checklist={checklist}
        loading={checklistLoading}
        onOpenDocument={handleOpenDeliverableDocument}
        onRefresh={handleRefreshChecklist}
      />
      <div
        ref={splitContainerRef}
        className={`flex flex-col gap-3 lg:flex-row lg:gap-0 ${
          documentsExpanded ? 'min-h-0 flex-1' : 'min-h-[900px]'
        } ${isSplitResizing ? 'select-none' : ''}`}
      >
        <div
          className="flex w-full flex-col rounded border border-gray-200 bg-white lg:min-w-0 lg:shrink-0 lg:w-[var(--u3121-docs-split-left)]"
          style={
            {
              '--u3121-docs-split-left': `${leftPct}%`,
            } as React.CSSProperties
          }
        >
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2">
            {canScan ? (
              <button
                type="button"
                className="shrink-0 rounded-lg border border-primary-600 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
                disabled={scanning || !projectId?.trim()}
                onClick={() => void handleScanFolder()}
              >
                {t('btnScanProjectFolder', { ns: NS })}
              </button>
            ) : null}
            {showNavigator ? (
              <div
                className="inline-flex rounded-lg border border-gray-300 p-0.5 text-sm"
                role="group"
                aria-label={t('lblDocumentTree', { ns: NS })}
              >
                <button
                  type="button"
                  className={`rounded-md px-2.5 py-1 font-medium ${
                    viewMode === 'tree'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setViewMode('tree')}
                >
                  {t('lblDocumentTree', { ns: NS })}
                </button>
                <button
                  type="button"
                  className={`rounded-md px-2.5 py-1 font-medium ${
                    viewMode === 'list'
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setViewMode('list')}
                >
                  {t('lblDocumentList', { ns: NS })}
                </button>
              </div>
            ) : null}
            {!documentsExpanded && onEnterDocumentsExpanded ? (
              <button
                type="button"
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={onEnterDocumentsExpanded}
              >
                {t('btnDocumentsEnterExpanded', { ns: NS })}
              </button>
            ) : null}
            {treeLoaded && tree ? (
              <div className="ml-auto min-w-0">
                <U3121NasSyncStatus sync={tree.nasSync} t={t} />
              </div>
            ) : null}
          </div>
          {showNavigator && viewMode === 'list' ? (
            <div className="flex flex-col gap-2 border-b border-gray-200 px-3 py-2">
              <div className="flex gap-2">
                <select
                  className="shrink-0 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  value={nameMatchMode}
                  onChange={(e) =>
                    setNameMatchMode(e.target.value as U3121DocumentNameMatchMode)
                  }
                  aria-label={t('lblDocumentNameMatchMode', { ns: NS })}
                >
                  {NAME_MATCH_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {t(nameMatchModeLabelKey(mode), { ns: 'common' })}
                    </option>
                  ))}
                </select>
                <input
                  type="search"
                  className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  value={nameQuery}
                  onChange={(e) => setNameQuery(e.target.value)}
                  placeholder={t('phDocumentNameFilter', { ns: NS })}
                  aria-label={t('phDocumentNameFilter', { ns: NS })}
                />
              </div>
              {availableTags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {availableTags.map((tag) => {
                    const active = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                          active
                            ? 'border-primary-600 bg-primary-600 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        aria-pressed={active}
                        onClick={() => toggleTag(tag)}
                      >
                        {t(documentTypeTagLabelKey(tag), { ns: NS, defaultValue: tag })}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="min-h-[240px] flex-1 overflow-auto p-2">
            {treeLoading && (
              <p className="p-2 text-sm text-gray-500">{t('msgDocumentTreeLoading', { ns: NS })}</p>
            )}
            {!treeLoading && isEmpty && (
              <p className="p-2 text-sm text-gray-500">{t('msgDocumentTreeEmpty', { ns: NS })}</p>
            )}
            {showNavigator && viewMode === 'tree' && (
              <U3121ProjectDocumentTree
                nodes={nodes}
                selectedPath={selectedPath}
                onSelectFile={handleSelectFile}
              />
            )}
            {showNavigator && viewMode === 'list' && filteredFlatItems.length === 0 && (
              <p className="p-2 text-sm text-gray-500">
                {t('msgDocumentListEmptyFilter', { ns: NS })}
              </p>
            )}
            {showNavigator && viewMode === 'list' && filteredFlatItems.length > 0 && (
              <U3121ProjectDocumentFlatList
                items={filteredFlatItems}
                selectedPath={selectedPath}
                projetNumber={tree?.projetNumber}
                onSelectFile={handleSelectFlatFile}
              />
            )}
          </div>
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={Math.round(leftPct)}
          aria-valuemin={5}
          aria-valuemax={95}
          aria-label={t('lblDocumentPreview', { ns: NS })}
          className={`relative z-10 hidden w-1.5 shrink-0 touch-none cursor-col-resize bg-gray-200 hover:bg-primary-400 lg:block ${
            isSplitResizing ? 'bg-primary-500' : ''
          }`}
          onPointerDown={handleSplitPointerDown}
        />
        <div className="flex w-full min-w-0 flex-col rounded border border-gray-200 bg-white lg:flex-1">
          <div className="border-b border-gray-200 px-3 py-2 text-sm font-medium text-gray-800">
            {t('lblDocumentPreview', { ns: NS })}
          </div>
          <div className={`flex-1 ${documentsExpanded ? 'min-h-0' : 'min-h-[820px]'}`}>
            <U3121ProjectDocumentPreviewPanel
              mode={previewMode}
              pdfObjectUrl={pdfObjectUrl}
              meta={meta}
              fileName={fileName}
              errorMessage={errorMessage}
              disableIframePointerEvents={isSplitResizing}
              onExpandPreview={
                previewMode === 'pdf' && pdfObjectUrl
                  ? () => setExpandPreviewOpen(true)
                  : undefined
              }
            />
          </div>
        </div>
      </div>
      {expandPreviewOpen && pdfObjectUrl ? (
        <U3121ProjectDocumentPreviewExpandDialog
          isOpen={expandPreviewOpen}
          onClose={() => setExpandPreviewOpen(false)}
          pdfObjectUrl={pdfObjectUrl}
          fileName={fileName}
        />
      ) : null}
    </div>
  );
};

export default U3121ProjectDocumentsBody;
